/**
 * Trigger config of a timeseries-aggregate data state (`properties.binding.options.timeAggregation.trigger`):
 * `{ onStateUpdate, schedule: null | {repeat, minute, hour?, weekDay?, monthDay?, timeZone} }`.
 * Mirrors backend/unify-service/mixins/database-models/lib/aggregationTrigger.js, which documents the shape.
 */
import momentTZ from 'moment-timezone';
import cloneDeep from 'lodash/cloneDeep';

export const REPEATS = ['hourly', 'daily', 'weekly', 'monthly'];

export const DEFAULT_TRIGGER = Object.freeze({ onStateUpdate: true, schedule: null });

// defaults used when a schedule is added or its repeat type changes
export const DEFAULT_SCHEDULE_HOUR = 6;
export const DEFAULT_SCHEDULE_MINUTE = 0;
export const DEFAULT_SCHEDULE_WEEKDAY = 1; // Monday
export const DEFAULT_SCHEDULE_MONTHDAY = 1;

// month days offered in the UI: 29..31 would silently skip shorter months
export const MAX_MONTH_DAY_OPTION = 28;

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;
const SECONDS_PER_WEEK = 7 * SECONDS_PER_DAY;

const isInt = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;

/** Normalised copy: always `{onStateUpdate: boolean, schedule: object|null}`. */
export function normalizeTrigger(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TRIGGER };
  }
  return {
    onStateUpdate: raw.onStateUpdate !== false,
    schedule: raw.schedule && typeof raw.schedule === 'object' ? cloneDeep(raw.schedule) : null,
  };
}

export function isValidTimeZone(timeZone) {
  return typeof timeZone === 'string' && !!momentTZ.tz.zone(timeZone);
}

/** Fields a repeat type does not use are ignored. */
export function isScheduleValid(schedule) {
  if (!schedule || typeof schedule !== 'object') {
    return false;
  }
  if (!REPEATS.includes(schedule.repeat)) {
    return false;
  }
  if (!isInt(schedule.minute, 0, 59)) {
    return false;
  }
  if (schedule.repeat !== 'hourly' && !isInt(schedule.hour, 0, 23)) {
    return false;
  }
  if (schedule.repeat === 'weekly' && !isInt(schedule.weekDay, 0, 6)) {
    return false;
  }
  if (schedule.repeat === 'monthly' && !isInt(schedule.monthDay, 1, 31)) {
    return false;
  }
  return isValidTimeZone(schedule.timeZone);
}

/** Valid when the state is still recalculated somehow: reactively, or by a valid schedule. */
export function isTriggerValid(trigger) {
  const normalized = normalizeTrigger(trigger);
  if (normalized.schedule) {
    return isScheduleValid(normalized.schedule);
  }
  return normalized.onStateUpdate;
}

/** 5-field cron expression of a valid schedule (display only; the backend derives its own). */
export function scheduleToCron(schedule) {
  if (!isScheduleValid(schedule)) {
    return null;
  }
  switch (schedule.repeat) {
    case 'hourly':
      return `${schedule.minute} * * * *`;
    case 'daily':
      return `${schedule.minute} ${schedule.hour} * * *`;
    case 'weekly':
      return `${schedule.minute} ${schedule.hour} * * ${schedule.weekDay}`;
    case 'monthly':
      return `${schedule.minute} ${schedule.hour} ${schedule.monthDay} * *`;
    default:
      return null;
  }
}

/** Fill the fields a repeat type needs with defaults (used when the repeat type is switched). */
export function ensureScheduleFields(schedule) {
  const result = { ...schedule };
  if (!isInt(result.minute, 0, 59)) {
    result.minute = DEFAULT_SCHEDULE_MINUTE;
  }
  if (result.repeat !== 'hourly' && !isInt(result.hour, 0, 23)) {
    result.hour = DEFAULT_SCHEDULE_HOUR;
  }
  if (result.repeat === 'weekly' && !isInt(result.weekDay, 0, 6)) {
    result.weekDay = DEFAULT_SCHEDULE_WEEKDAY;
  }
  if (result.repeat === 'monthly' && !isInt(result.monthDay, 1, MAX_MONTH_DAY_OPTION)) {
    result.monthDay = DEFAULT_SCHEDULE_MONTHDAY;
  }
  return result;
}

/** Repeat type that fits an aggregation duration (seconds or a `now-1d/d`-like range expression). */
export function repeatForDuration(duration) {
  if (typeof duration === 'string') {
    const match = /\/[0-9]*([hdwMy])$/.exec(duration);
    const unit = match ? match[1] : null;
    if (unit === 'h') {
      return 'hourly';
    }
    if (unit === 'd') {
      return 'daily';
    }
    if (unit === 'w') {
      return 'weekly';
    }
    if (unit === 'M' || unit === 'y') {
      return 'monthly';
    }
    return 'daily';
  }
  const seconds = Number(duration);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'daily';
  }
  if (seconds <= SECONDS_PER_HOUR) {
    return 'hourly';
  }
  if (seconds <= SECONDS_PER_DAY) {
    return 'daily';
  }
  if (seconds <= SECONDS_PER_WEEK) {
    return 'weekly';
  }
  return 'monthly';
}

/** Initial schedule for an aggregation: aligned to its duration, in its timezone. */
export function defaultScheduleForDuration(duration, timeZone) {
  return ensureScheduleFields({
    repeat: repeatForDuration(duration),
    minute: DEFAULT_SCHEDULE_MINUTE,
    timeZone: isValidTimeZone(timeZone) ? timeZone : momentTZ.tz.guess(),
  });
}

/** Next instant (strictly after `from`) a valid schedule fires, or null. Without the backend's per-twin slot offset. */
export function nextRun(schedule, from = new Date()) {
  if (!isScheduleValid(schedule)) {
    return null;
  }
  const now = momentTZ.tz(from, schedule.timeZone);
  const base = now.clone().second(0).millisecond(0);
  let candidate;
  switch (schedule.repeat) {
    case 'hourly':
      candidate = base.clone().minute(schedule.minute);
      if (!candidate.isAfter(now)) {
        candidate.add(1, 'hour');
      }
      return candidate.toDate();
    case 'daily':
      candidate = base.clone().hour(schedule.hour).minute(schedule.minute);
      if (!candidate.isAfter(now)) {
        candidate.add(1, 'day');
      }
      return candidate.toDate();
    case 'weekly':
      // moment `.day(n)` is Sunday-based regardless of locale, same as weekDay
      candidate = base.clone().day(schedule.weekDay).hour(schedule.hour).minute(schedule.minute);
      if (!candidate.isAfter(now)) {
        candidate.add(1, 'week');
      }
      return candidate.toDate();
    case 'monthly':
      for (let i = 0; i <= 12; i++) {
        const month = base.clone().date(1).add(i, 'month');
        if (month.daysInMonth() >= schedule.monthDay) {
          const run = month.date(schedule.monthDay).hour(schedule.hour).minute(schedule.minute);
          if (run.isAfter(now)) {
            return run.toDate();
          }
        }
      }
      return null;
    default:
      return null;
  }
}

export function formatTime(hour, minute) {
  const pad = (n) => `${n}`.padStart(2, '0');
  return `${pad(hour)}:${pad(minute)}`;
}

/** `"06:05"` -> `{hour: 6, minute: 5}`, null for anything that is not HH:mm. */
export function parseTime(value) {
  const match = /^([01][0-9]|2[0-3]):([0-5][0-9])/.exec(`${value || ''}`);
  if (!match) {
    return null;
  }
  return { hour: parseInt(match[1], 10), minute: parseInt(match[2], 10) };
}

/** `1st` / `2nd` / `3rd` in English, `1.` otherwise. */
export function ordinalDay(day, language) {
  if (`${language || ''}`.toLowerCase().startsWith('en')) {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) {
      return `${day}th`;
    }
    switch (day % 10) {
      case 1:
        return `${day}st`;
      case 2:
        return `${day}nd`;
      case 3:
        return `${day}rd`;
      default:
        return `${day}th`;
    }
  }
  return `${day}.`;
}
